import React, { useState } from 'react';
import {
  Box,
  Avatar,
  Typography,
  TextField,
  Button,
  Paper,
  styled,
  CssBaseline,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { register } from '../services/auth.service';
import Banner from '../components/common/Banner';

const StyledPageContainer = styled(Box)({
  minHeight: '100vh',
  width: '100vw',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

const StyledAddUserCard = styled(Paper)({
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  borderRadius: '24px',
  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15), 0 10px 20px rgba(102, 126, 234, 0.1)',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '440px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
});

const StyledHeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '32px',
  gap: '16px',
});

const StyledAvatar = styled(Avatar)({
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
  width: '64px',
  height: '64px',
});

const StyledTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '28px',
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
  marginBottom: '8px',
});

const StyledTextField = styled(TextField)({
  marginBottom: '24px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    '&:hover fieldset': {
      borderColor: '#667eea',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#667eea',
  },
});

const StyledButton = styled(Button)({
  borderRadius: '12px',
  padding: '14px 28px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
  marginBottom: '24px',
  '&:hover': {
    background: 'linear-gradient(145deg, #5a6fd8, #6a42a0)',
    boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
});

const StyledSuccessMessage = styled(Box)({
  textAlign: 'center',
  padding: '32px 24px',
  borderRadius: '16px',
  background: 'linear-gradient(145deg, #e8f5e8, #f0f8f0)',
  border: '2px solid #4caf50',
  marginTop: '16px',
});

export default function AddUser() {
  const [message, setMessage] = useState<any>();
  const [severity, setSeverity] = useState<any>();
  const [email, setEmail] = useState<any>();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const response = await register(String(data.get('email')));
    if (response instanceof Error) {
      setMessage(response.message);
      setSeverity('error');
    } else {
      setMessage('User is added successfully.');
      setSeverity('success');
      setEmail('');
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setMessage(''); // Clear any previous messages
  };

  return (
    <>
      <CssBaseline />
      <StyledPageContainer>
        <StyledAddUserCard elevation={0}>
          <StyledHeaderContainer>
            <StyledAvatar>
              <LockOutlinedIcon fontSize="large" />
            </StyledAvatar>
            <StyledTitle>
              Add New User
            </StyledTitle>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              sx={{ fontWeight: 400, opacity: 0.8 }}
            >
              Enter an email address to invite a new user to the platform
            </Typography>
          </StyledHeaderContainer>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <StyledTextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              value={email}
              onChange={handleEmailChange}
              autoComplete="email"
              autoFocus
              variant="outlined"
              placeholder="Enter user's email address"
            />
            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
            >
              Add User
            </StyledButton>
          </Box>

          {severity === 'success' && (
            <StyledSuccessMessage>
              <Typography variant="h6" color="success.main" gutterBottom>
                User Added Successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                An invitation has been sent to the email address.
              </Typography>
            </StyledSuccessMessage>
          )}

          <Banner message={message} severity={severity} />
        </StyledAddUserCard>
      </StyledPageContainer>
    </>
  );
}
